import { registerGlobalValue, cache } from '../utils/registerGlobalValue';
import { DeviceInfo } from '../typings/DeviceInfo';
import { JSBridgeReturnType } from '../typings/JSBridgeProtocol';
import { getSafelyWindow } from '../utils/safelyWindow';
import { StaticData } from '../typings/StaticData';
export enum CallTypeEnum {
  callAndBackfeed = 'callAndBack',
  callAndListen = 'callAndListen',
}

/**
 *
 * 将原本 JSBridgeApp 中的逻辑均聚合至此 Controller 实现，
 * 目的是规避太多的引用对象被暴露（防止因引用对象被'篡改'造成未知异常）。
 *
 */
export class WKWebViewController {
  worker?: Worker;
  deviceInfo?: DeviceInfo;
  staticData?: StaticData;

  constructor() {
    this.checkBridgeReady();
  }

  private checkBridgeReady(timeout = 6000) {
    if (this.worker) return true;

    const start = performance.now();

    return new Promise<boolean>((resolve, reject) => {
      const check = () => {
        if (this.worker) return resolve(true);

        const $window = getSafelyWindow();

        if (
          $window?.webkit &&
          $window?.webkit.messageHandlers &&
          $window?.webkit.messageHandlers.realsee
        ) {
          const worker = $window?.webkit.messageHandlers.realsee;

          worker?.postMessage(
            JSON.stringify({
              type: 'getStaticData',
              param: JSON.stringify({
                functionName: registerGlobalValue(
                  'HYBRID_BRIDGE_NAME',
                  (data: any) => {
                    this.staticData = JSON.parse(data) as StaticData;
                    this.deviceInfo = this.staticData;
                    this.worker = worker;
                    return resolve(true);
                  },
                ),
              }),
            }),
          );

          return;
        }
        if (performance.now() - start > (timeout || 6000)) {
          // console.warn('HybridBridge 初始化超时~')
          return reject(new Error('HybridBridge 初始化超时~'));
        }

        // 逐帧监测
        requestAnimationFrame(check);
      };

      // 开启监测
      check();
    });
  }

  async checkReady(timeout = 6000) {
    const res: JSBridgeReturnType<boolean> = [null, null];

    if (this.worker) {
      res[0] = true;
      return res;
    }

    try {
      res[0] = await this.checkBridgeReady(timeout);
    } catch (error) {
      res[0] = false;
      res[1] = error as Error;
    }

    return res;
  }

  async getStaticData(timeout = 6000): Promise<JSBridgeReturnType<StaticData | false>> {
    const [ready, readyError] = await this.checkReady(timeout);
    if (!ready) return [false, readyError];
    return [this.staticData || false, null];
  }

  get ready() {
    return !!this.worker;
  }

  get scheme() {
    return this.deviceInfo?.scheme || '';
  }

  createSchemeLink(schemeUrl: string) {
    if (schemeUrl.startsWith(this.scheme)) {
      return schemeUrl;
    }
    return this.scheme + '://' + schemeUrl;
  }

  async callAndBackfeed<T>(scheme: string) {
    const res = [false, null] as JSBridgeReturnType<T | false>;
    const [ready, readyError] = await this.checkReady();

    if (!ready) {
      res[0] = false;
      res[1] = readyError;

      return res;
    }

    return await new Promise<JSBridgeReturnType<T | false>>(async (resolve) => {
      const actionUrl = this.createSchemeLink(scheme);

      const msg = JSON.stringify({
        actionUrl,
        functionName: registerGlobalValue(
          this.createSchemeLink(scheme),
          (data: string) => {
            const d = (() => {
              try {
                return JSON.parse(data);
              } catch (e) {
                return data;
              }
            })();

            res[0] = (() => {
              if (d === 1) return true;
              if (d === 0) return false;
              return d;
            })() as T;

            resolve(res);
          },
        ),
      });

      this.worker!.postMessage(
        JSON.stringify({
          type: 'callAndBackfeed',
          param: msg,
        }),
      );
    });
  }

  async callAndListen<T>(scheme: string, callback: (data: T) => void) {
    const res = [false, null] as JSBridgeReturnType<false | (() => void)>;
    const [ready, readyError] = await this.checkReady();

    if (!ready) {
      res[0] = false;
      res[1] = readyError;

      return res;
    }

    return await new Promise<JSBridgeReturnType<false | (() => void)>>(
      async (resolve) => {
        const actionUrl = this.createSchemeLink(scheme);
        const _callback = (data: string) => {
          const d = (() => {
            try {
              return JSON.parse(data);
            } catch (e) {
              return data;
            }
          })();

          const callbackParam = (() => {
            if (d === 1) return true;
            if (d === 0) return false;
            return d;
          })() as T;

          callback(callbackParam);
          res[0] = () => {
            // 清理掉当前的回调引用
            const callbacks = cache[actionUrl];
            if (!callbacks) return;

            const idx = callbacks.queue.findIndex(
              (func) => func.callback === _callback,
            );
            if (idx !== -1) {
              callbacks.queue.splice(idx, 1);
            }
          };
          resolve(res);
        };

        const msg = JSON.stringify({
          actionUrl,
          functionName: registerGlobalValue(actionUrl, _callback, false),
        });

        this.worker!.postMessage(
          JSON.stringify({
            type: 'callAndListen',
            param: msg,
          }),
        );
      },
    );
  }
}
